/**
 * URL validation utilities to prevent open redirect vulnerabilities
 */

/**
 * Validates if a URL is safe for redirects
 * @param url - The URL to validate
 * @param allowedDomains - Array of allowed domains (optional)
 * @returns boolean indicating if the URL is safe
 */
export const isValidRedirectUrl = (
  url: string,
  allowedDomains?: string[]
): boolean => {
  if (!url || typeof url !== "string") {
    return false;
  }

  try {
    const urlObj = new URL(url);

    // Check if it's a relative URL (safe)
    if (!urlObj.protocol && !urlObj.hostname) {
      return true;
    }

    // Check if it's HTTPS (preferred)
    if (urlObj.protocol !== "https:" && urlObj.protocol !== "http:") {
      return false;
    }

    // If allowedDomains is provided, check against it
    if (allowedDomains && allowedDomains.length > 0) {
      return allowedDomains.some(
        (domain) =>
          urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
      );
    }

    // Default: only allow same-origin redirects
    return urlObj.origin === window.location.origin;
  } catch (error) {
    // Invalid URL format
    return false;
  }
};

/**
 * Validates if a path is safe for internal redirects
 * @param path - The path to validate (e.g., "/sites/123/editcamera/456")
 * @returns boolean indicating if the path is safe
 */
export const isValidInternalPath = (path: string): boolean => {
  if (!path || typeof path !== "string") {
    return false;
  }

  // Must start with /
  if (!path.startsWith("/")) {
    return false;
  }

  // Check for dangerous patterns
  const dangerousPatterns = [
    "//", // Double slashes (potential protocol-relative URLs)
    "javascript:", // JavaScript URLs
    "data:", // Data URLs
    "vbscript:", // VBScript URLs
    "file:", // File URLs
    "ftp:", // FTP URLs
    "mailto:", // Mailto URLs
    "tel:", // Tel URLs
  ];

  const lowerPath = path.toLowerCase();
  for (const pattern of dangerousPatterns) {
    if (lowerPath.includes(pattern)) {
      return false;
    }
  }

  // Check for suspicious characters
  const suspiciousChars = ["<", ">", '"', "'", "`", "\\"];
  for (const char of suspiciousChars) {
    if (path.includes(char)) {
      return false;
    }
  }

  return true;
};

/**
 * Validates if a path corresponds to a valid route in the application
 * @param path - The path to validate
 * @returns boolean indicating if the path matches a known route pattern
 */
export const isValidAppRoute = (path: string): boolean => {
  if (!isValidInternalPath(path)) {
    return false;
  }

  // Remove query parameters and hash for route matching
  const cleanPath = path.split("?")[0].split("#")[0];

  // Define valid route patterns based on App.jsx routes
  const validRoutePatterns = [
    // Exact matches
    "/",
    "/login",
    "/dashboard",
    "/sites",
    "/sites_patrol",
    "/clips",
    "/patrols",
    "/patrols_test",
    "/smtp",
    "/calendars",
    "/alerts",
    "/analytics",
    "/user",
    "/groupuser",
    "/health",
    "/productrequirements",
    "/callback",
    "/eecallback",
    "/MFA",
    "/no-internet",

    // Dynamic routes with parameters
    /^\/sites\/addsite$/,
    /^\/sites\/cameracheck$/,
    /^\/sites\/addCamera$/,
    /^\/sites\/autoaddcamera$/,
    /^\/sites\/addschedule$/,
    /^\/sites\/addschedulesecret$/,
    /^\/sites\/aboutsite\/\d+$/,
    /^\/sites\/aboutsitesecret\/\d+$/,
    /^\/sites\/\d+\/videowall$/,
    /^\/sites\/\d+\/editcamera\/\d+\/editignorezones$/,
    /^\/sites\/\d+\/editcamera\/\d+\/ignorezones$/,
    /^\/sites\/\d+\/editcamera\/\d+\/editmetadatatagzones$/,
    /^\/sites\/\d+\/editcamera\/\d+$/,
    /^\/actionlogs\/\d+$/,
    /^\/alerts&.*$/,
    /^\/analytics&.*$/,
  ];

  // Check against exact matches first
  if (validRoutePatterns.some((pattern) => pattern === cleanPath)) {
    return true;
  }

  // Check against regex patterns
  return validRoutePatterns.some((pattern) => {
    if (pattern instanceof RegExp) {
      return pattern.test(cleanPath);
    }
    return false;
  });
};

/**
 * Safely redirects to a URL after validation
 * @param url - The URL to redirect to
 * @param allowedDomains - Array of allowed domains (optional)
 * @param fallbackUrl - Fallback URL if validation fails
 */
export const safeRedirect = (
  url: string,
  allowedDomains?: string[],
  fallbackUrl: string = "/dashboard"
): void => {
  if (isValidRedirectUrl(url, allowedDomains)) {
    window.location.href = url;
  } else {
    console.warn(`Blocked unsafe redirect to: ${url}`);
    window.location.href = fallbackUrl;
  }
};
